import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { StationAutocomplete } from "@/components/StationAutocomplete";

/**
 * RegularPlacesEditor — the "Regular mileage" list in Settings → Travel.
 * Places the user drives to that aren't the gig itself (e.g. the station they
 * park at). Each shows up as a home ↔ place leg in the gig mileage calculator.
 * Props:
 *  - places: [{ id, name, place_id, lat, lng, miles }]
 *  - onChange: (nextPlaces) => void
 *
 * `miles` is one-way from home. Left blank, the backend fills in the driving
 * distance from the home address when the travel settings are saved.
 */
export const RegularPlacesEditor = ({ places = [], onChange }) => {
  // Location picked for the next place, before it's added to the list.
  const [newPlace, setNewPlace] = useState(null);

  const addPlace = () => {
    if (!newPlace?.name) return;
    onChange([
      ...places,
      {
        id: `rp_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
        name: newPlace.name,
        place_id: newPlace.place_id || null,
        lat: newPlace.lat ?? null,
        lng: newPlace.lng ?? null,
        miles: null,
      },
    ]);
    setNewPlace(null);
  };

  const updatePlace = (id, changes) =>
    onChange(places.map((p) => (p.id === id ? { ...p, ...changes } : p)));

  return (
    <div className="space-y-3">
      {places.length > 0 && (
        <div className="space-y-2" data-testid="regular-places-list">
          {places.map((place) => (
            <div key={place.id} className="flex items-center gap-2">
              <Input
                value={place.name}
                onChange={(e) => updatePlace(place.id, { name: e.target.value })}
                className="flex-1 min-w-0"
                aria-label="Place name"
              />
              <div className="relative w-24 shrink-0">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Auto"
                  value={place.miles ?? ""}
                  onChange={(e) => updatePlace(place.id, {
                    miles: e.target.value === "" ? null : parseFloat(e.target.value),
                  })}
                  className="pr-8"
                  aria-label="One-way miles from home"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">mi</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onChange(places.filter((p) => p.id !== place.id))}
                className="shrink-0 text-muted-foreground hover:text-red-400"
                aria-label={`Remove ${place.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Miles are one-way from home. Leave blank and save to calculate the driving
            distance from your home address.
          </p>
        </div>
      )}

      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <StationAutocomplete
            value={newPlace}
            onChange={setNewPlace}
            placeholder="Search for a place, e.g. Reading station"
            testId="regular-place-search"
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={addPlace}
          disabled={!newPlace?.name}
          data-testid="add-regular-place"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
};

export default RegularPlacesEditor;
